import { Component, OnInit, OnDestroy, HostListener, signal, computed, inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ListingService } from '../../core/services/listing.service';
import { HomeService } from '../../core/services/home.service';
import { Announcement } from '../../core/models/announcement.model';
import { ResidentialComplex, Banner, ListingFilter } from '../../core/models/home.model';
import { FilterPanelComponent } from '../../shared/components/filter-panel/filter-panel.component';
import { ListingCardComponent } from '../../shared/components/listing-card/listing-card.component';
import { ComplexCardComponent } from '../../shared/components/complex-card/complex-card.component';
import { SideBannerComponent } from '../../shared/components/side-banner/side-banner.component';
import { SkeletonCardComponent } from '../../shared/components/skeleton-card/skeleton-card.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FilterPanelComponent,
    ListingCardComponent,
    ComplexCardComponent,
    SideBannerComponent,
    SkeletonCardComponent
  ]
})
export class HomeComponent implements OnInit, OnDestroy {
  // Signals for state management
  filters = signal<ListingFilter>({});
  isFiltered = computed(() => {
    const f = this.filters();
    return !!(f.dealType || f.propertyType || (f.rooms && f.rooms.length > 0) || f.minPrice || f.maxPrice || f.cityId || f.districtId || f.settlementId || f.metroId || f.location);
  });

  // Data
  vipListings = signal<Announcement[]>([]);
  allListings = signal<Announcement[]>([]);
  complexes = signal<ResidentialComplex[]>([]);
  banners = signal<Banner[]>([]);

  // UI State
  loadingVip = signal(false);
  loadingAll = signal(false);
  loadingComplexes = signal(false);

  // Pagination
  vipPage = 1;
  allPage = 1;
  pageSize = 12;
  hasMoreVip = true;
  hasMoreAll = true;

  private destroy$ = new Subject<void>();
  private platformId = inject(PLATFORM_ID);

  constructor(
    private listingService: ListingService,
    private homeService: HomeService
  ) {}

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.loadInitialData();
      this.loadAllListings();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadInitialData() {
    this.loadingComplexes.set(true);
    this.homeService.getLatestComplexes(4).subscribe(res => {
      this.complexes.set(res);
      this.loadingComplexes.set(false);
    });

    this.homeService.getHomeBanners().subscribe(res => {
      this.banners.set(res);
    });

    this.loadVipListings();
  }

  loadVipListings() {
    if (this.loadingVip() || !this.hasMoreVip) return;

    this.loadingVip.set(true);
    this.listingService.getVipListings(this.vipPage, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (res.length < this.pageSize) this.hasMoreVip = false;
        this.vipListings.update(prev => [...prev, ...res]);
        this.vipPage++;
        this.loadingVip.set(false);
      });
  }

  loadAllListings(reset = false) {
    if (reset) {
      this.allPage = 1;
      this.allListings.set([]);
      this.hasMoreAll = true;
    }

    if (this.loadingAll() || !this.hasMoreAll) return;

    this.loadingAll.set(true);
    this.listingService.getAllListings(this.filters(), this.allPage, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (res.length < this.pageSize) this.hasMoreAll = false;
        this.allListings.update(prev => [...prev, ...res]);
        this.allPage++;
        this.loadingAll.set(false);
      });
  }

  onFilterChanged(newFilters: ListingFilter) {
    this.filters.set(newFilters);
    if (this.isFiltered()) {
      this.loadAllListings(true);
    }
  }

  onAreaModalApplied(newFilters: ListingFilter) {
    this.filters.set(newFilters);
    this.loadAllListings(true);
  }

  onResetFilters() {
    this.filters.set({});
    this.loadAllListings(true);
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (isPlatformBrowser(this.platformId)) {
      const pos = (document.documentElement.scrollTop || document.body.scrollTop) + document.documentElement.offsetHeight;
      const max = document.documentElement.scrollHeight;

      if (pos > max - 800) {
        this.loadAllListings();
        if (!this.isFiltered()) {
          this.loadVipListings();
        }
      }
    }
  }

  getBanner(position: 'left' | 'right') {
    return this.banners().find(b => b.position === position);
  }
}
